import { Portfolio } from "api/initial/useGetContactInfo";
import { useModifiedTranslation } from "hooks/useModifiedTranslation";
import { isProfessionalClassification } from "utils/userClassification";
import { ProfileClassificationCode, SecurityClassType1, SecurityDetailsPosition, SecurityTypeCode } from "../../api/holdings/types";
import { useGetFinancialProfile } from "../../api/trading/useGetFinancialProfile";


export interface ConfirmationCheckInfo {
    title: string;
    info: string;
    label: string;
}

export const useGetConfirmationCheckInfo = (
    selectedPortfolio? : Portfolio, 
    security?: SecurityDetailsPosition | null
): ConfirmationCheckInfo | undefined => {
    const { t } = useModifiedTranslation();
    const shortName = selectedPortfolio?.shortName;
    const securityType: string = security?.type?.code as string;
    const securityClassType1Value = security?.classType1?.value;
    // if shortName is undefined, return false
    const extID = shortName ? shortName.split('-')[0] : '';
    const { data: financialProfile, loading: loadingFinancialProfile } = useGetFinancialProfile(extID);

    const today = new Date();
    today.setHours(0,0,0,0);

    const isFutureOrTodayDate = (d?: string | null | undefined) => {
        if (!d) return false;
        const x = new Date(d);
        x.setHours(0,0,0,0);
        return x >= today;
    };

    if (loadingFinancialProfile || !financialProfile || !financialProfile.contacts || financialProfile.contacts.length === 0) {
        return undefined;
    }

    //If Customer classification is PROF (Professional) - checkbox never will be displayed 
    if(isProfessionalClassification(financialProfile.contacts[0]?.classification?.code)) {
        return undefined;
    }

    //show only for "Customer is RET (Retail)"
    if(financialProfile.contacts[0]?.classification?.code !== ProfileClassificationCode.RET) {
        return undefined;
    }

    //If Security is Non-Complex - do not check test result, checkbox never will be displayed
    if(securityClassType1Value === SecurityClassType1.NON_COMPLEX) {
        return undefined;
    }

    //show only Security is Complex
    if(securityClassType1Value !== SecurityClassType1.COMPLEX) {
        return undefined;
    }

    const profile = financialProfile.contacts[0].profile;

    
    const etfTestResult = profile.etfTestResult?.stringValue;


    if (securityType === SecurityTypeCode.DEBT_INSTRUMENT && securityClassType1Value === SecurityClassType1.COMPLEX) {
        const riskUnderstTestResult = profile.riskUnderstTestResult?.stringValue;
        const derivativesTestResult = profile.derivativesTestResult?.stringValue;
        
        const atLeastOneTest = !!derivativesTestResult && !!riskUnderstTestResult
        && (derivativesTestResult === "NOT OK" || riskUnderstTestResult === "NOT OK")
        && !!profile.derivativesExpiryDate?.dateValue && !!profile.riskUnderstExpiryDate?.dateValue
        && isFutureOrTodayDate(profile?.derivativesExpiryDate?.dateValue)
        && isFutureOrTodayDate(profile?.riskUnderstExpiryDate?.dateValue);

        // only SecType:10 
        if (
            security?.tagsAsSet?.includes("SecType:10") && !security?.tagsAsSet?.includes("SecType:11")
            && derivativesTestResult === "NOT OK" && !!profile.derivativesExpiryDate?.dateValue && isFutureOrTodayDate(profile?.derivativesExpiryDate?.dateValue)
        ) {    
            return {
                title: t("tradingModal.confirmationWarningTitleDEBT10"),
                info: t("tradingModal.confirmationWarningTextDEBT10"),
                label: t("tradingModal.confirmationLabelDEBT10")
            }
        } 


        //only "SecType:11" 
        if (
            !security?.tagsAsSet?.includes("SecType:10") && security?.tagsAsSet?.includes("SecType:11")
            && riskUnderstTestResult === "NOT OK" && !!profile.riskUnderstExpiryDate?.dateValue && isFutureOrTodayDate(profile?.riskUnderstExpiryDate?.dateValue)
        ) {
            return {
                title: t("tradingModal.confirmationWarningTitleDEBT11"),
                info: t("tradingModal.confirmationWarningTextDEBT11"),
                label: t("tradingModal.confirmationLabelDEBT11")
            }
        }
        
        // "SecType:10" and "SecType:11" 
        if (
            security?.tagsAsSet?.includes("SecType:10") && security?.tagsAsSet?.includes("SecType:11")
            && atLeastOneTest
        ) {
            return {
                title: t("tradingModal.confirmationWarningTitleDEBT10_11"),
                info: t("tradingModal.confirmationWarningTextDEBT10_11"),
                label: t("tradingModal.confirmationLabelDEBT10_11")
            }
        }
    }

    if (securityType === SecurityTypeCode.ETF) {
        if (profile && !!profile.etfExpiryDate?.dateValue 
            && isFutureOrTodayDate(profile?.etfExpiryDate?.dateValue) 
            && !!etfTestResult && etfTestResult === "NOT OK") {
                return {
                    title: t("tradingModal.confirmationWarningTitleETF"),
                    info: t("tradingModal.confirmationWarningTextETF"),
                    label: t("tradingModal.confirmationLabelETF")
                }
            }
    }

    return undefined;
};
